// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Plugins
import {
  convertPressure,
} from '../../../../../../../../plugins/general';
import {
  booleanStatusToTagType,
  booleanToOnOffText,
} from '../../../../../../../../plugins/crusher';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../../../store/measurement.store';

// Types
import {
  MeasurementType,
  PressureType,
} from '../../../../../../../../types/global';

// Components
import { EquipmentSensorListItem } from '../../../../../../../../components/EquipmentSensorList';
import { Row } from '../../../../../../../../layout/elements/Row';
import { Tag } from '../../../../../../../../layout/elements/Tag';

// Modules
import { ListModule } from '../../../ListModule';

// View
import { HydraulicProps } from './Hydraulic.types';
import { Container, ListCol, ListContainer } from './Hydraulic.style';

function Hydraulic({
  className,
  unitData,
  edgeStatus,
  testId,
}: HydraulicProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  const pressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);

  const trampReleaseList: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.startPressurizeDescription'),
      setpoint: convertPressure(
        unitData?.spTrampPressureStartPressurize,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.stopPressurizeDescription'),
      setpoint: convertPressure(
        unitData?.spTrampPressureStopPressurize,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.lowAlarmDescription'),
      setpoint: convertPressure(
        unitData?.spTrampPressureLowAlarm,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const clampPressureList: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.startPressurizeDescription'),
      setpoint: convertPressure(
        unitData?.spClampPressureStartPressurize,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.stopPressurizeDescription'),
      setpoint: convertPressure(
        unitData?.spClampPressureStopPressurize,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.lowAlarmDescription'),
      setpoint: convertPressure(
        unitData?.spClampPressureLowAlarm,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  const bowlList: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.maxClampPressureDescription'),
      setpoint: convertPressure(
        unitData?.spBowlAdjustmentMaxClampPressure,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.bowlAdjustmentDescription'),
      setpoint: (
        <Tag
          text={booleanToOnOffText(unitData?.spBowlAdjustmentUnderLoadBool)}
          type={booleanStatusToTagType(
            unitData?.spBowlAdjustmentUnderLoadBool,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )}
        />
      ),
    },
  ];

  const pressureList: EquipmentSensorListItem[] = [
    {
      sensor: t('view.crusher.maintenance.setPoint.hydraulic.systemPressureDescription'),
      setpoint: convertPressure(
        unitData?.spSystemPressureHighAlarm,
        pressureUnit,
        0,
        true,
      ).formattedValue,
    },
  ];

  return (
    <Container className={className} data-testid={testId}>
      <Row>
        <ListContainer>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.hydraulic.trampPressureTitle')}
              items={trampReleaseList}
            />
          </ListCol>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.hydraulic.clampPressureTitle')}
              items={clampPressureList}
            />
          </ListCol>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.hydraulic.bowlAdjustmentTitle')}
              items={bowlList}
            />
          </ListCol>
          <ListCol cols={24} lg={12}>
            <ListModule
              title={t('view.crusher.maintenance.setPoint.hydraulic.systemPressureTitle')}
              items={pressureList}
            />
          </ListCol>
        </ListContainer>
      </Row>
    </Container>
  );
}

export { Hydraulic };
